import { useState, FormEvent } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Add recipient email
    formData.append('recipient', 'matt@epicrealestate.com');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4re84cpu0j44avv3dh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div 
        className="bg-[#0f1f3a] rounded-lg max-w-lg w-full p-8 relative border border-[#DC2626]/20"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-mail-line text-2xl text-[#DC2626]"></i>
            </div>
            <h2 className="text-white font-black text-2xl" style={{ fontFamily: "'Oswald', sans-serif" }}>
              CONTACT US
            </h2>
          </div>
          <p className="text-gray-400 text-base">
            Have questions? Send us a message and we'll get back to you soon.
          </p>
        </div>

        {/* Form */}
        <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-white font-semibold mb-2 text-sm">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="w-full px-4 py-3 bg-[#0a1628] border border-[#DC2626]/20 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-[#DC2626] transition-colors text-sm"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-white font-semibold mb-2 text-sm">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full px-4 py-3 bg-[#0a1628] border border-[#DC2626]/20 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-[#DC2626] transition-colors text-sm"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-white font-semibold mb-2 text-sm">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="w-full px-4 py-3 bg-[#0a1628] border border-[#DC2626]/20 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-[#DC2626] transition-colors text-sm"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-white font-semibold mb-2 text-sm">
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 bg-[#0a1628] border border-[#DC2626]/20 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-[#DC2626] transition-colors resize-none text-sm"
              placeholder="How can we help you?"
            />
            <p className="text-gray-500 text-xs mt-1">Maximum 500 characters</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black py-4 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-base"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-md p-3 text-center">
              <p className="text-green-400 text-sm font-semibold">Message sent successfully!</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-md p-3 text-center">
              <p className="text-red-400 text-sm font-semibold">Failed to send. Please try again.</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
